import { useState } from 'react';
import { RefreshCw, WifiOff } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader } from '@reflex/ui/components/ui/card';
import { Button } from '@reflex/ui/components/ui/button';
import { PanelTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { cn } from '@reflex/ui/lib/utils';
import { authCardClassName } from './AuthLayout';

interface ConnectionErrorCardProps {
  /**
   * Re-read the session. Resolves once the attempt finishes, whether or not
   * it succeeded: on success the auth state flips and this card unmounts, so
   * "did it work?" is answered by the card disappearing, not by a return
   * value.
   */
  onRetry: () => Promise<void>;
}

/**
 * Shown on the auth screen when the app could not read `/auth/me` at all
 * (the server was unreachable, or answered with something that is not an
 * auth status).
 *
 * Without this, a failed read left the login screen rendering an empty
 * provider list under "No authentication providers are configured", which
 * blames the instance's configuration for what is usually a dropped
 * connection and gives the visitor nothing to act on.
 */
export function ConnectionErrorCard({ onRetry }: ConnectionErrorCardProps) {
  const [isRetrying, setIsRetrying] = useState(false);
  const [attempts, setAttempts] = useState(0);

  async function handleRetry() {
    setIsRetrying(true);
    try {
      await onRetry();
    } finally {
      setIsRetrying(false);
      setAttempts((n) => n + 1);
    }
  }

  return (
    <Card
      role="alert"
      className={cn(authCardClassName, 'relative overflow-hidden')}
      data-testid="auth-offline"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 -top-px h-32 bg-gradient-to-b from-destructive/[0.08] to-transparent"
      />
      <CardHeader className="relative items-center text-center">
        {/* CardHeader lays its children out on a grid, so the badge needs its
            own full-width row to sit on the card's center axis. */}
        <div className="flex justify-center">
          <span className="flex size-11 items-center justify-center rounded-full border border-border/60 bg-muted/30">
            <WifiOff className="icon-lg text-muted-foreground" aria-hidden="true" />
          </span>
        </div>
        <PanelTitle className="mt-4 text-2xl tracking-tight">Can't reach Reflex</PanelTitle>
        <CardDescription className="mx-auto max-w-[19rem] text-balance leading-relaxed">
          We couldn't load the sign-in options. Check your connection, then try again.
        </CardDescription>
      </CardHeader>
      <CardContent className="relative flex flex-col gap-3">
        <Button
          size="lg"
          onClick={() => void handleRetry()}
          disabled={isRetrying}
          data-testid="auth-offline-retry"
        >
          <RefreshCw className={cn('icon-md', isRetrying && 'animate-spin')} aria-hidden="true" />
          {isRetrying ? 'Retrying…' : 'Try again'}
        </Button>
        {/* Announced rather than silent: the button label is the only other
            feedback a retry produces, and a screen reader would miss it. */}
        <div aria-live="polite">
          {attempts > 0 && !isRetrying && (
            <div data-testid="auth-offline-retry-failed">
              <PageDescription className="text-center text-xs">
                Still no luck. We'll keep trying in the background and retry the moment your
                connection is back.
              </PageDescription>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
